import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logEvent } from '@amplitude/analytics-browser';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import CircularProgress from '@mui/material/CircularProgress';

import TopBar from '../../componentsv2/TopBar';
import MarcoLogo from '../../componentsv2/MarcoLogo';
import Typography from '../../componentsv2/Typography';
import InfoButton from '../../componentsv2/InfoButton';
import { CrossIcon } from '../../components/Icons';
import SimpleBackdrop from '../../components/Backdrop';

import { getCompanyLegalRepresentatives, getCompanyShareholders } from '../../services/documents';

import { useAppSelector } from '../../store';
import { useDocuments } from '../../hooks/documents';
import { useLazyTranslation } from '../../hooks/i18n';
import { useUserLocation } from '../../hooks/useUserLocation';

import { INVOICE_FINANCING, filesURL } from '../../constants/auth';
import { docsCollectionEvents } from '../../constants/events';

import { APIStakeholder, CategoryGroup, DocumentType, IDocumentCategory } from '../../types/documents';
import { CompanyUser, UserRole } from '../../types/companyUser';

import DocumentCategories from './DocumentCategories';
import LegalRepresentatives from './LegalRepresentatives';
import Shareholders from './Shareholders';

const CATEGORY_ORDER = ['BASIC_DOCUMENTS', 'BANK', 'FINANCES', 'COMMERCE', 'MARCO_DOCUMENTS'];

function getGuideUrl(country: string, language: string) {
  if (country === 'mx') return `${filesURL}/Documents_Guide_MX.pdf`;
  return `${filesURL}/Documents_Guide_${language.toUpperCase()}.pdf`;
}

const CompanyUserDocuments = ({
  showTopBar = true,
  showStakeholders = true,
}: {
  showTopBar?: boolean;
  showStakeholders?: boolean;
}) => {
  const { t, isLoading: isLoadingTranslations } = useLazyTranslation(['common', 'client']);
  const { selectedCompany } = useAppSelector((state) => state.companyUser.companyUser) || ({} as CompanyUser);
  const isVisualizer = useAppSelector(
    (state) => state.companyUser.companyUser?.selectedCompanyRole === UserRole.VISUALIZER
  );
  const navigate = useNavigate();
  const { language } = useUserLocation();
  const [shareholders, setShareholders] = useState<APIStakeholder[]>([]);
  const [legalRepresentatives, setLegalRepresentatives] = useState<APIStakeholder[]>([]);
  const [isLoadingShareholders, setIsLoadingShareholders] = useState(false);
  const [isLoadingLegalRepresentatives, setIsLoadingLegalRepresentatives] = useState(false);

  const companyId = selectedCompany.id;

  const {
    loadCompanyCategories,
    groupedCategoriesByDocType,
    uploadAssets,
    isLoading,
    deleteAsset,
    downloadAssetsByDocumentId,
  } = useDocuments(companyId, language);

  // Get all Company Documents
  const companyDocuments: IDocumentCategory[] = Object.values(
    groupedCategoriesByDocType[DocumentType.COMPANY]?.groupedCategories || {}
  )
    .flat()
    .map((category) => ({
      ...category,
      readOnly: true,
    }));

  const invFinCategories: Record<string, IDocumentCategory[]> =
    groupedCategoriesByDocType?.[DocumentType.GENERIC]?.groupedCategories || {};

  if (invFinCategories[CategoryGroup.BASIC_DOCUMENTS]) {
    invFinCategories[CategoryGroup.BASIC_DOCUMENTS].push(...companyDocuments);
  }

  // Sort categories according to the defined order
  const sortedInvFinCategories: Record<string, IDocumentCategory[]> = {};
  CATEGORY_ORDER.forEach((category) => {
    if (invFinCategories[category]) {
      sortedInvFinCategories[category] = invFinCategories[category];
    }
  });

  // Add any remaining categories that weren't in the order list
  Object.keys(invFinCategories).forEach((category) => {
    if (!CATEGORY_ORDER.includes(category)) {
      sortedInvFinCategories[category] = invFinCategories[category];
    }
  });

  const loadShareholders = (companyId: number) => {
    setIsLoadingShareholders(true);
    return getCompanyShareholders(companyId)
      .then((shareholders) => {
        setShareholders(shareholders);
      })
      .finally(() => {
        setIsLoadingShareholders(false);
      });
  };

  const loadLegalRepresentatives = (companyId: number) => {
    setIsLoadingLegalRepresentatives(true);
    return getCompanyLegalRepresentatives(companyId)
      .then((legalRepresentatives) => {
        setLegalRepresentatives(legalRepresentatives);
      })
      .finally(() => {
        setIsLoadingLegalRepresentatives(false);
      });
  };

  useEffect(() => {
    logEvent(docsCollectionEvents.VIEW_DOCUMENTS_PAGE);
  }, []);

  useEffect(() => {
    if (companyId) {
      loadCompanyCategories();
      showStakeholders && loadShareholders(companyId);
      showStakeholders && loadLegalRepresentatives(companyId);
    }
  }, [companyId, showStakeholders]);

  if (isLoadingTranslations || isLoading || !companyId || !selectedCompany)
    return showTopBar ? (
      <SimpleBackdrop hasOpaqueBackground isLoading />
    ) : (
      <Box sx={{ display: 'grid', minHeight: '100vh', placeItems: 'center', width: '100%' }}>
        <CircularProgress size={40} />
      </Box>
    );

  const guideUrl = getGuideUrl(selectedCompany.country, language);

  return (
    <>
      <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
        {showTopBar && (
          <TopBar>
            <Box sx={{ margin: 'auto', width: '80%', display: 'flex' }}>
              <IconButton href="/" sx={{ flex: 1 }} disableRipple>
                <MarcoLogo />
              </IconButton>
              <IconButton
                onClick={() => {
                  navigate(INVOICE_FINANCING);
                }}
              >
                <CrossIcon />
              </IconButton>
            </Box>
          </TopBar>
        )}
        <Box component="main" sx={{ flexGrow: 1, backgroundColor: '#F1F6FD', paddingTop: '2em' }}>
          <Container>
            <Grid container alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography
                  sx={{
                    fontSize: '24px',
                    fontWeight: 600,
                    background: 'linear-gradient(255.79deg, #4572B8 0%, #2FC0B8 95.35%)',
                    '-webkit-background-clip': 'text',
                    '-webkit-text-fill-color': 'transparent',
                  }}
                >
                  {t('client:documents.title')}
                </Typography>
                <Typography>{t('client:documents.subtitle')}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: { xs: 'initial', md: 'flex-end' },
                  }}
                >
                  <InfoButton title={t('client:documents.guideInfoButton')} />
                  <a
                    href={guideUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => {
                      logEvent(docsCollectionEvents.CLICK_VIEW_GUIDE, {
                        Version: guideUrl.split('/').slice(-1),
                      });
                    }}
                    style={{ color: '#4F55FD' }}
                  >
                    <Typography sx={{ color: '#4F55FD', fontWeight: '600' }}>
                      {t('client:documents.buttons.viewGuide')}
                    </Typography>
                  </a>
                </Box>
              </Grid>
              {showStakeholders && (
                <>
                  <Grid item xs={12}>
                    {isLoadingShareholders ? (
                      <Skeleton variant="rectangular" height={80} animation="wave" />
                    ) : (
                      <Shareholders
                        shareholders={shareholders}
                        companyId={companyId}
                        loadShareholders={loadShareholders}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    {isLoadingLegalRepresentatives ? (
                      <Skeleton variant="rectangular" height={80} animation="wave" />
                    ) : (
                      <LegalRepresentatives
                        legalRepresentatives={legalRepresentatives}
                        companyId={companyId}
                        loadLegalRepresentatives={loadLegalRepresentatives}
                      />
                    )}
                  </Grid>
                </>
              )}
              <DocumentCategories
                groupedCategories={sortedInvFinCategories}
                companyId={companyId}
                language={language}
                loadCompanyCategories={loadCompanyCategories}
                uploadAssets={uploadAssets}
                deleteAsset={deleteAsset}
                downloadDocument={downloadAssetsByDocumentId}
                isVisualizer={isVisualizer}
              />
            </Grid>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default CompanyUserDocuments;
